// src/constants/pageSEO.js

import constants from "./constants";

const pageSEO = {
  // Home page SEO
  home: () => ({
    title: "Home",
    description: `${constants.BROKERAGE_NAME} - Professional real estate services to help you find your dream home. We offer property search, market analysis, and expert realtor services.`,
    keywords:
      "real estate, realtor, property, home, house, real estate services",
    path: "/",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: constants.BROKERAGE_NAME,
      url:
        constants.ENVIRONMENT === "production"
          ? `https://${constants.DOMAIN}`
          : `http://${constants.URL}`,
      logo: "/logo.png",
      contactPoint: {
        "@type": "ContactPoint",
        telephone: constants.BROKERAGE_NUMBER,
        contactType: "customer service",
      },
    },
  }),

  // Calculator page SEO
  calculator: () => ({
    title: "Mortgage Calculator",
    description:
      "Use our mortgage calculator to estimate your monthly payments, total interest costs, and affordability. Make informed financial decisions.",
    keywords:
      "mortgage calculator, loan calculator, monthly payments, interest, home buying budget",
    path: "/calculator",
  }),

  // Listing page SEO (dynamic based on listing data)
  listing: (listing) => ({
    title: `${listing?.address || 'Property'} - ${listing?.location || 'Location'}`,
    description: `${listing.bedrooms} bedroom ${listing.bathrooms} bathroom ${listing.propertyType} located in ${listing.location}, priced at ${listing.price}.`,
    keywords: `${listing.location}, ${listing.propertyType}, ${listing.bedrooms} bedroom, ${listing.bathrooms} bathroom, property`,
    path: `/listing/${listing.id}`,
    image: listing.mainImage,
    type: "product",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      name: listing.address,
      description: listing.description,
      image: listing.mainImage,
      offers: {
        "@type": "Offer",
        priceCurrency: "CAD",
        price: listing?.price?.replace(/[^\d.]/g, ""),
        availability: "https://schema.org/InStock",
      },
    },
  }),

  // Listings page SEO
  listings: () => ({
    title: "Property Listings",
    description:
      "Browse our curated selection of properties, including condos, single-family homes, and townhouses in Toronto and surrounding areas.",
    keywords:
      "property listings, real estate, homes for sale, Toronto properties, condos, houses",
    path: "/listings",
  }),

  // About page SEO
  about: () => ({
    title: "About Us",
    description: `Learn about ${constants.BROKERAGE_NAME} and our team of professional real estate agents. Discover our commitment to excellence in real estate services.`,
    keywords:
      "about us, real estate company, real estate team, professional agents",
    path: "/about",
  }),

  // Evaluation page SEO
  evaluation: () => ({
    title: "Property Evaluation",
    description:
      "Get a free market evaluation of your property. Understand your property's value in the current market and make informed buying or selling decisions.",
    keywords:
      "property evaluation, home value, market analysis, real estate assessment",
    path: "/evaluation",
  }),

  // Reports page SEO
  reports: () => ({
    title: "Real Estate Market Reports",
    description:
      "Latest real estate market trends, analysis, and forecasts. Learn about housing price trends, inventory levels, and market dynamics in different areas.",
    keywords:
      "real estate market, market reports, price trends, market analysis, real estate forecast",
    path: "/real-estate-reports",
  }),

  // Contact page SEO
  contact: () => ({
    title: "Contact Us",
    description: `Contact the real estate team at ${constants.BROKERAGE_NAME} for professional advice on buying, selling, or investing in real estate.`,
    keywords: "contact, real estate, agent, realtor, property consultation",
    path: "/contact",
  }),

  // Agents page SEO
  agents: () => ({
    title: "Our Real Estate Agents",
    description:
      "Meet our team of professional real estate agents. Each agent brings extensive experience and expertise to help you buy or sell your property.",
    keywords:
      "real estate agents, realtors, property experts, real estate professionals",
    path: "/agents",
  }),

  // Commercial page SEO
  commercial: () => ({
    title: "Commercial Real Estate",
    description:
      "Browse our commercial property listings, including office spaces, retail storefronts, and industrial properties. Professional commercial real estate services.",
    keywords:
      "commercial real estate, office space, retail space, investment property, commercial property",
    path: "/commercial",
  }),

  // Seller Guide page SEO
  sellerGuide: () => ({
    title: "Seller's Guide",
    description:
      "Professional guide to selling your home, providing tips on property preparation, pricing strategy, and marketing techniques to get the best price.",
    keywords:
      "home selling, seller's guide, property sales, real estate market, pricing",
    path: "/selling-guide",
  }),

  // Buyer Guide page SEO
  buyerGuide: () => ({
    title: "Buyer's Guide",
    description:
      "Comprehensive guide to buying a home, helping you understand the buying process, financing options, and factors to consider.",
    keywords:
      "home buying, buyer's guide, real estate, buying process, mortgage",
    path: "/buying-guide",
  }),

  // Location page SEO (dynamic based on location data)
  location: (location) => ({
    title: `${location.name} Real Estate and Properties`,
    description: `Browse the latest properties, real estate market trends, and community information in ${location.name}. Learn about housing prices, schools, and local amenities in ${location.name}.`,
    keywords: `${location.name}, real estate, properties, homes for sale, ${location.name} community, ${location.name} housing market`,
    path: `/location/${location.slug}`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Place",
      name: location.name,
      description: `Browse the latest properties, real estate market trends, and community information in ${location.name}.`,
    },
  }),
};

export default pageSEO;
